import { apiClient } from '@/lib/api/client'
import type { Transaction } from '@/types'

export interface CreateTransactionPayload {
  account?: number
  from_account?: number
  to_account?: number
  category?: number // Category ID; savings and transfers do not use categories.
  amount: string
  fee?: string
  description: string
  date: string
  end_date?: string | null
  actionType: 'INCOME' | 'EXPENSE' | 'SAVING' | 'TRANSFER'
  uploaded_files?: File[]
}

export interface UpdateTransactionPayload {
  id: number
  account?: number
  from_account?: number
  to_account?: number
  category?: number
  amount: string
  fee?: string
  description: string
  date: string
  end_date?: string | null
  actionType: 'INCOME' | 'EXPENSE' | 'SAVING' | 'TRANSFER'
  uploaded_files?: File[]
}

export const createTransaction = async (
  payload: CreateTransactionPayload
): Promise<Transaction> => {
  const { actionType, uploaded_files, category, ...basePayload } = payload
  const dataPayload = category === undefined ? basePayload : { ...basePayload, category }
  
  let endpoint = ''
  switch (actionType) {
    case 'INCOME':
      endpoint = '/api/income/'
      break
    case 'EXPENSE':
      endpoint = '/api/expenses/'
      break
    case 'SAVING':
      endpoint = '/api/savings/'
      break
    case 'TRANSFER':
      endpoint = '/api/transfers/'
      break
  }

  // If we have files, we must use FormData
  if (uploaded_files && uploaded_files.length > 0) {
    const formData = new FormData()
    
    // Append all standard fields
    Object.entries(dataPayload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, String(value))
      }
    })
    
    // Append all files under the 'uploaded_files' key
    uploaded_files.forEach(file => {
      formData.append('uploaded_files', file)
    })

    const { data } = await apiClient.post(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return data
  }

  // Otherwise, standard JSON POST
  const { data } = await apiClient.post(endpoint, dataPayload)
  return data
}

export const updateTransaction = async (
  payload: UpdateTransactionPayload
): Promise<Transaction> => {
  const { id, actionType, uploaded_files, category, ...basePayload } = payload
  const dataPayload = category === undefined ? basePayload : { ...basePayload, category }

  let endpoint = ''
  switch (actionType) {
    case 'INCOME':
      endpoint = `/api/income/${id}/`
      break
    case 'EXPENSE':
      endpoint = `/api/expenses/${id}/`
      break
    case 'SAVING':
      endpoint = `/api/savings/${id}/`
      break
    case 'TRANSFER':
      endpoint = `/api/transfers/${id}/`
      break
  }

  if (uploaded_files && uploaded_files.length > 0) {
    const formData = new FormData()
    Object.entries(dataPayload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, String(value))
      }
    })
    uploaded_files.forEach(file => {
      formData.append('uploaded_files', file)
    })

    const { data } = await apiClient.patch(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return data
  }

  const { data } = await apiClient.patch(endpoint, dataPayload)
  return data
}

export const deleteTransaction = async (
  id: number,
  actionType: 'INCOME' | 'EXPENSE' | 'SAVING' | 'TRANSFER'
): Promise<void> => {
  let endpoint = ''
  switch (actionType) {
    case 'INCOME':
      endpoint = `/api/income/${id}/`
      break
    case 'EXPENSE':
      endpoint = `/api/expenses/${id}/`
      break
    case 'SAVING':
      endpoint = `/api/savings/${id}/`
      break
    case 'TRANSFER':
      endpoint = `/api/transfers/${id}/`
      break
  }
  await apiClient.delete(endpoint)
}

export interface TransactionQueryParams {
  year?: number
  month?: number
  category?: number
  account?: number
  search?: string
  date_from?: string
  date_to?: string
  ordering?: string
}

export const getExpenses = async (params?: TransactionQueryParams): Promise<Transaction[]> => {
  const { data } = await apiClient.get('/api/expenses/', { params })
  return data
}

export const getIncomes = async (params?: TransactionQueryParams): Promise<Transaction[]> => {
  const { data } = await apiClient.get('/api/income/', { params })
  return data
}

export const getSavings = async (params?: TransactionQueryParams): Promise<Transaction[]> => {
  const { data } = await apiClient.get('/api/savings/', { params })
  return data
}

export const getTransfers = async (params?: TransactionQueryParams): Promise<Transaction[]> => {
  const { data } = await apiClient.get('/api/transfers/', { params })
  return data
}

export interface AvailableMonth {
  value: string
  year: number
  month: number
  label: string
}

export const getAvailableMonths = async (): Promise<AvailableMonth[]> => {
  const { data } = await apiClient.get<AvailableMonth[]>('/api/finances/months/')
  return data
}



